// frontend/src/services/pushNotificationService.js
import { isNativePlatform } from './platform';
import api from './api';

let registered = false;

/**
 * Register for push notifications on native platforms.
 * Requests permission, gets device token, and sends it to the backend.
 * @param {string} authToken - JWT auth token for API calls
 */
export async function registerPushNotifications(authToken) {
  if (!isNativePlatform() || registered) return;

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const { Device } = await import('@capacitor/device');

    // Check current permission status
    const permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'denied') return;

    // Request permission if not yet granted
    if (permStatus.receive !== 'granted') {
      const result = await PushNotifications.requestPermissions();
      if (result.receive !== 'granted') return;
    }

    const deviceInfo = await Device.getId();
    const deviceId = deviceInfo.identifier;

    // Listen for registration success
    PushNotifications.addListener('registration', async (token) => {
      try {
        await api.post('/api/notifications/push-token', {
          token: token.value,
          platform: 'ios',
          deviceId
        }, {
          headers: { 'x-auth-token': authToken }
        });
        registered = true;
      } catch (err) {
        console.error('Failed to register push token with backend:', err);
      }
    });

    // Listen for registration errors
    PushNotifications.addListener('registrationError', (err) => {
      console.error('Push registration failed:', err);
    });

    // Handle foreground notifications
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push received in foreground:', notification);
    });

    // Handle notification tap
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('Push notification tapped:', action);
      const data = action.notification?.data;
      if (data?.screen) {
        // Navigate to the specified screen
        window.location.hash = `#/${data.screen.toLowerCase()}`;
      }
    });

    // Trigger registration with APNs
    await PushNotifications.register();
  } catch (err) {
    console.error('Error setting up push notifications:', err);
  }
}

/**
 * Unregister push token from backend (call on logout)
 * @param {string} authToken
 */
export async function unregisterPushNotifications(authToken) {
  if (!isNativePlatform()) return;

  try {
    const { Device } = await import('@capacitor/device');
    const deviceInfo = await Device.getId();

    await api.delete('/api/notifications/push-token', {
      data: { deviceId: deviceInfo.identifier },
      headers: { 'x-auth-token': authToken }
    });
    registered = false;
  } catch (err) {
    console.error('Failed to unregister push token:', err);
  }
}
